import type { ProgressQuestion, ProgressQuota, ProgressTarget } from "@/types";
import type { QuestionRow, QuotaRow, QuotaStatus, TargetRow } from "./types";

type TitledQuestion = ProgressQuestion & { title?: string };

const percentage = (value: number, total: number): number => (total > 0 ? Math.round((value / total) * 100) : 0);

const quotaNTarget = (quota: ProgressQuota): number => quota.target || ((quota.target_value ?? 0) !== 0 ? 1 : 0);

const isFollowUp = (quota: ProgressQuota): boolean => quota.quota_type === "follow_up";

const quotaMinTolerance = (quota: ProgressQuota): number | null =>
  quota.tolerance_min_percent ?? quota.global_min_tolerance ?? null;

function quotaStatus(
  nCurrent: number,
  nTarget: number,
  minValue: number,
  followUp: boolean,
): { status: QuotaStatus; remaining: number } {
  if (followUp) return { status: "follow_up", remaining: 0 };
  if (nTarget > 0 && nCurrent >= nTarget) return { status: "full", remaining: 0 };
  if (minValue > 0 && nCurrent < minValue) return { status: "below_min", remaining: minValue - nCurrent };
  return { status: "to_target", remaining: Math.max(nTarget - nCurrent, 0) };
}

function toQuotaRow(quota: ProgressQuota): QuotaRow {
  const nTarget = quotaNTarget(quota);
  const nCurrent = quota.n_total ?? 0;
  const minValue = Number(quota.min) || 0;
  return {
    id: String(quota.id),
    name: quota.name || "--",
    nCurrent,
    nTarget,
    percentage: percentage(nCurrent, nTarget),
    minTolerance: quotaMinTolerance(quota),
    minValue,
    isFollowUp: isFollowUp(quota),
    ...quotaStatus(nCurrent, nTarget, minValue, isFollowUp(quota)),
  };
}

function toQuestionRow(question: TitledQuestion): QuestionRow {
  const quotas = question.quotas ?? [];
  return {
    name: question.name || "--",
    title: question.title ?? "",
    isFollowUp: quotas.some(isFollowUp),
    quotas: quotas.map(toQuotaRow),
  };
}

function toTargetRow(target: ProgressTarget): TargetRow {
  const questions = ((target.questions ?? []) as TitledQuestion[]).map(toQuestionRow);
  return {
    id: String(target.id),
    name: target.name || "--",
    nCurrent: target.n_current ?? 0,
    nTarget: target.n_target ?? 0,
    percentage: percentage(target.n_current ?? 0, target.n_target ?? 0),
    questions,
    hasQuotas: questions.some((question) => question.quotas.length > 0),
  };
}

// The API rounds each target's min, so the global row rounds once from the summed tolerances.
interface GlobalQuota {
  name: string;
  nCurrent: number;
  nTarget: number;
  toleranceHundredths: number;
}

const globalMinValue = (agg: GlobalQuota): number => agg.nTarget - Math.floor(agg.toleranceHundredths / 100);

const globalMinTolerance = (agg: GlobalQuota): number | null =>
  agg.nTarget > 0 ? Math.round(agg.toleranceHundredths / agg.nTarget) : null;

function buildGlobalRow(targets: ProgressTarget[]): TargetRow | null {
  if (targets.length <= 1) return null;

  const questions = new Map<
    string,
    { name: string; title: string; hasStandard: boolean; quotas: Map<string, GlobalQuota> }
  >();
  let nCurrent = 0;
  let nTarget = 0;

  for (const target of targets) {
    nCurrent += target.n_current ?? 0;
    nTarget += target.n_target ?? 0;

    for (const question of (target.questions ?? []) as TitledQuestion[]) {
      const group = questions.get(question.card_token) ?? {
        name: question.name || "--",
        title: question.title ?? "",
        hasStandard: false,
        quotas: new Map<string, GlobalQuota>(),
      };
      questions.set(question.card_token, group);

      for (const quota of question.quotas ?? []) {
        if (!isFollowUp(quota)) group.hasStandard = true;
        const key = String(quota.answer);
        const agg = group.quotas.get(key) ?? {
          name: quota.name || "--",
          nCurrent: 0,
          nTarget: 0,
          toleranceHundredths: 0,
        };
        agg.nCurrent += quota.n_total ?? 0;
        agg.nTarget += quotaNTarget(quota);
        agg.toleranceHundredths += quotaNTarget(quota) * (quotaMinTolerance(quota) ?? 0);
        group.quotas.set(key, agg);
      }
    }
  }

  const questionRows: QuestionRow[] = Array.from(questions.entries())
    .map(([token, group]) => ({
      name: group.name,
      title: group.title,
      isFollowUp: !group.hasStandard,
      quotas: Array.from(group.quotas.entries()).map(([key, agg]) => ({
        id: `global-${token}-${key}`,
        name: agg.name,
        nCurrent: agg.nCurrent,
        nTarget: agg.nTarget,
        percentage: percentage(agg.nCurrent, agg.nTarget),
        minTolerance: globalMinTolerance(agg),
        minValue: globalMinValue(agg),
        isFollowUp: !group.hasStandard,
        ...quotaStatus(agg.nCurrent, agg.nTarget, globalMinValue(agg), !group.hasStandard),
      })),
    }))
    .filter((question) => question.quotas.length > 0);

  if (questionRows.length === 0) return null;

  return {
    id: "global",
    name: "All targets",
    nCurrent,
    nTarget,
    percentage: percentage(nCurrent, nTarget),
    questions: questionRows,
    hasQuotas: true,
  };
}

export function buildRows(targets: ProgressTarget[]): TargetRow[] {
  const globalRow = buildGlobalRow(targets);
  const targetRows = targets.map(toTargetRow);
  return globalRow ? [globalRow, ...targetRows] : targetRows;
}
